import { Link } from "react-router-dom";
import {
  renderCustomColumnDefs,
  renderValidations,
} from "../../shared/Table/table.utils";
import { capitalizeFirstLetter, formatDate } from "../../utils";
import { Badge, Popover } from "../../shared";
import { getEquipmentComplianceAlerts } from "../../utils/equipmentCompliance";
import { getAvailabilityBadge } from "../../utils/equipmentAvailability";
import { TagBadgeList } from "../../components/Tags";
import { hasStoredPermission } from "../../utils/authority";
import { PERMISSIONS } from "../../constants/access-control";

// Config for table and form
const tableConfig = [
  {
    accessorKey: "name",
    id: "name",
    visible: true,
    required: true,
    minWidth: 200,
    editable: hasStoredPermission(PERMISSIONS.EQUIPMENT_EDIT),
    cell: ({ getValue, row: { original } }) => {
      return (
        <Link
          className="text-primary-600 hover:underline"
          to={`/equipment/${original._id}`}
        >
          {getValue()}
        </Link>
      );
    },
  },
  {
    id: "equipmentCategory",
    header: "Category",
    enableGlobalFilter: true,
    visible: true,
    cell: (info) => {
      const value = info.getValue();
      return value ? <Badge label={capitalizeFirstLetter(value)}></Badge> : "-";
    },
  },
  {
    id: "type",
    enableGlobalFilter: true,
    visible: true,
    cell: (info) => {
      const value = info.getValue();
      return value ? <Badge label={capitalizeFirstLetter(value)}></Badge> : "-";
    },
  },
  {
    id: "status",
    enableGlobalFilter: true,
    visible: true,
    cell: (info) => {
      const value = info.getValue();
      return <Badge label={capitalizeFirstLetter(value)}></Badge>;
    },
  },
  {
    id: "availability",
    header: "Availability",
    visible: true,
    minWidth: 160,
    cell: ({ row: { original } }) => {
      const badge = getAvailabilityBadge(original.availability);
      return <Badge label={badge.label} color={badge.color} />;
    },
  },
  {
    id: "tags",
    header: "Tags",
    visible: true,
    minWidth: 140,
    cell: ({ row: { original } }) => {
      const tags = original.tags || [];
      if (!tags.length) return "-";
      return <TagBadgeList tags={tags} emptyText="" size="sm" />;
    },
  },
  {
    id: "pmStatus",
    header: "PM",
    visible: true,
    cell: ({ row: { original } }) => {
      const schedules = original.maintenanceSchedules || [];
      if (!schedules.length) return "-";
      const overdue = schedules.some(
        (row) =>
          row.enabled !== false &&
          row.nextDueAt &&
          new Date(row.nextDueAt) < new Date()
      );
      if (overdue) return <Badge label="Overdue" color="red" />;
      return <Badge label="Scheduled" color="green" />;
    },
  },
  {
    id: "compliance",
    header: "Compliance",
    visible: true,
    minWidth: 180,
    cell: ({ row: { original } }) => {
      const alerts = getEquipmentComplianceAlerts(original);
      if (!alerts.length) {
        return <Badge label="OK" color="green" />;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {alerts.map((alert) => (
            <Badge key={alert.key} label={alert.label} color={alert.color} />
          ))}
        </div>
      );
    },
  },
  {
    id: "model",
    cell: (info) => {
      const value = info.getValue();
      return value || "-";
    },
    enableGlobalFilter: true,
    visible: true,
    minWidth: 140,
  },
  {
    id: "manufacturer",
    cell: (info) => {
      const value = info.getValue();
      return value || "-";
    },
    enableGlobalFilter: true,
    visible: true,
    minWidth: 140,
  },
  {
    id: "serialNumber",
    cell: (info) => {
      const value = info.getValue();
      return value || "-";
    },
    enableGlobalFilter: true,
    visible: true,
    minWidth: 140,
  },
  {
    id: "vin",
    header: "VIN",
    cell: (info) => {
      const value = info.getValue();
      return value || "-";
    },
    enableGlobalFilter: true,
    visible: true,
    minWidth: 180,
  },
  {
    id: "licensePlate",
    header: "License Plate",
    cell: (info) => {
      const value = info.getValue();
      return value || "-";
    },
    enableGlobalFilter: true,
    visible: true,
    minWidth: 140,
  },
  {
    id: "year",
    header: "Year",
    cell: (info) => {
      const value = info.getValue();
      return value || "-";
    },
    enableGlobalFilter: true,
    visible: true,
    minWidth: 100,
  },
  {
    id: "purchaseDate",
    enableGlobalFilter: true,
    visible: true,
    cell: (info) => {
      const value = info.getValue();
      return value && formatDate(new Date(value), null, true);
    },
    width: 180,
  },
  {
    id: "lastMaintenanceDate",
    enableGlobalFilter: true,
    visible: true,
    cell: (info) => {
      const value = info.getValue();
      return value && formatDate(new Date(value), null, true);
    },
    width: 180,
  },
  {
    id: "nextMaintenanceDate",
    enableGlobalFilter: true,
    visible: true,
    cell: (info) => {
      const value = info.getValue();
      return value && formatDate(new Date(value), null, true);
    },
    width: 180,
  },
  {
    id: "notes",
    minWidth: 140,
  },
  {
    id: "_id",
    header: "id",
    visible: false,
    disableGlobalFilter: true,
  },
];

export const COLUMN_DEFINITIONS = renderCustomColumnDefs(tableConfig);
export const FORM_OPTIONS = {
  validations: renderValidations(tableConfig, "equipment"),
};
export const PAGE_SIZE_OPTIONS = [
  { label: "10 Equipment", value: 10 },
  { label: "20 Equipment", value: 20 },
  { label: "50 Equipment", value: 50 },
  { label: "100 Equipment", value: 100 },
  { label: "150 Equipment", value: 150 },
];
